import React, {useState} from "react";
import Layout from "../../../locales/en/layout/Layout";
import BigFotoBack from "../../../components/bigFotoBack/BigFotoBack";
import bg from "../../../assets/markazlar/ilmiyFaoliyat.jpg";
import {useTranslation} from "react-i18next";
import styled, {keyframes} from "styled-components";

/* ── animations ───────────────────────────────────────────── */
const fadeSlide = keyframes`
    from {
        opacity: 0;
        transform: translateY(14px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

/* ── page wrapper ─────────────────────────────────────────── */
const PageWrap = styled.div`
    background: #f5f7fa;
    min-height: 60vh;
    padding: 40px 0 80px;
`;

/* ── section title ────────────────────────────────────────── */
const SectionLabel = styled.p`
    font-size: 25px;
    font-weight: 600;
    color: #15523d;
    border-bottom: 2px solid #15523d;
    padding-bottom: 10px;
    margin-bottom: 28px;
    font-family: "Montserrat", sans-serif;
    letter-spacing: 0.3px;
`;

/* ── accordion year block ─────────────────────────────────── */
const YearBlock = styled.div`
    margin-bottom: 16px;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
    background: #fff;
`;

const YearHeader = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 24px;
    background: ${({open}) =>
            open
                    ? "linear-gradient(135deg, #15523d 0%, #1e7a58 100%)"
                    : "#fff"};
    color: ${({open}) => (open ? "#fff" : "#15523d")};
    border: none;
    cursor: pointer;
    font-family: "Montserrat", sans-serif;
    font-size: 17px;
    font-weight: 700;
    letter-spacing: 0.4px;
    transition: background 0.3s ease, color 0.3s ease;

    &:hover {
        background: linear-gradient(135deg, #0f3d2c 0%, #15523d 100%);
        color: #fff;
    }

    .year-info {
        display: flex;
        align-items: center;
        gap: 14px;

        .badge {
            background: ${({open}) =>
                    open ? "rgba(255,255,255,0.2)" : "rgba(21,82,61,0.1)"};
            color: ${({open}) => (open ? "#fff" : "#15523d")};
            border-radius: 20px;
            padding: 3px 12px;
            font-size: 12px;
            font-weight: 600;
        }
    }

    .chevron {
        transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        transform: ${({open}) => (open ? "rotate(180deg)" : "rotate(0deg)")};
        display: flex;
        align-items: center;
    }
`;

const YearBody = styled.div`
    overflow: hidden;
    max-height: ${({open}) => (open ? "2400px" : "0")};
    transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s ease;
    opacity: ${({open}) => (open ? 1 : 0)};
`;

const CardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 20px;
    padding: 24px 24px 28px;

    @media (max-width: 480px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 14px;
        padding: 16px;
    }
`;

/* ── flip card ────────────────────────────────────────────── */
const FlipScene = styled.div`
    display: block;
    height: 220px;
    perspective: 900px;
    text-decoration: none;
    animation: ${fadeSlide} 0.4s ease both;
    cursor: pointer;

    &:hover .flipper {
        transform: rotateY(180deg);
    }

    .flipper {
        position: relative;
        width: 100%;
        height: 100%;
        transition: transform 0.55s cubic-bezier(0.4, 0.2, 0.2, 1);
        transform-style: preserve-3d;
    }

    .face {
        position: absolute;
        inset: 0;
        border-radius: 14px;
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
        overflow: hidden;
    }

    /* ── FRONT ── */

    .front {
        background: #fff;
        border: 1.5px solid #e5eae8;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 10px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
        transition: box-shadow 0.3s;

        .front-icon {
            width: 56px;
            height: 56px;
            background: linear-gradient(145deg, #15523d, #1e7a58);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 6px 16px rgba(21, 82, 61, 0.3);
        }

        .front-label {
            font-family: "Montserrat", sans-serif;
            font-size: 12px;
            font-weight: 700;
            color: #1a2e25;
            text-align: center;
            padding: 0 10px;
            line-height: 1.4;
        }
    }

    &:hover .front {
        box-shadow: 0 12px 32px rgba(21, 82, 61, 0.18);
    }

    /* ── BACK ── */

    .back {
        background: linear-gradient(160deg, #0d3d2b 0%, #15523d 45%, #1e7a58 100%);
        transform: rotateY(180deg);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
        padding: 14px 12px 12px;
        box-shadow: 0 12px 32px rgba(21, 82, 61, 0.3);

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 4px;
            background: linear-gradient(90deg, #f0c040, #f5e06a, #f0c040);
            border-radius: 14px 14px 0 0;
        }

        .back-top {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
        }

        .back-brand {
            font-family: "Montserrat", sans-serif;
            font-size: 9px;
            font-weight: 700;
            letter-spacing: 1.5px;
            text-transform: uppercase;
            color: rgba(255, 255, 255, 0.6);
        }

        .back-type {
            font-family: "Montserrat", sans-serif;
            font-size: 8px;
            font-weight: 600;
            color: #f0c040;
            background: rgba(240, 192, 64, 0.15);
            border-radius: 4px;
            padding: 2px 6px;
            letter-spacing: 0.8px;
            text-transform: uppercase;
        }

        .back-title {
            font-family: "Montserrat", sans-serif;
            font-size: 11px;
            font-weight: 700;
            color: #fff;
            text-align: center;
            line-height: 1.4;
            padding: 0 4px;
        }

        .back-year {
            font-family: "Montserrat", sans-serif;
            font-size: 13px;
            font-weight: 600;
            color: rgba(255, 255, 255, 0.75);
            letter-spacing: 2px;
        }

        .back-actions {
            display: flex;
            gap: 6px;
            flex-wrap: wrap;
            justify-content: center;
        }

        .back-btn {
            background: rgba(255, 255, 255, 0.15);
            border: 1px solid rgba(255, 255, 255, 0.3);
            border-radius: 20px;
            padding: 5px 14px;
            font-size: 10px;
            font-weight: 700;
            color: #fff;
            font-family: "Montserrat", sans-serif;
            letter-spacing: 0.5px;
            backdrop-filter: blur(6px);
            text-decoration: none;
            display: inline-block;
        }

        .back-pdf {
            background: rgba(240, 192, 64, 0.25);
            border: 1px solid rgba(240, 192, 64, 0.6);
            border-radius: 20px;
            padding: 5px 12px;
            font-size: 10px;
            font-weight: 700;
            color: #f0c040;
            font-family: "Montserrat", sans-serif;
            letter-spacing: 0.5px;
            backdrop-filter: blur(6px);
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 3px;

            &:hover {
                background: rgba(240, 192, 64, 0.45);
            }
        }
    }
`;

/* ── chevron SVG ──────────────────────────────────────────── */
function ChevronIcon({color = "currentColor"}) {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
             stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"/>
        </svg>
    );
}

/* ── conference icon (front face) ────────────────────────── */
function ConferenceIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
             stroke="rgba(255,255,255,0.9)" strokeWidth="1.8"
             strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
    );
}

/* ── conference data ──────────────────────────────────────── */
const conferenceData = [
    {
        year: 2026,
        conferences: [
            {
                label: "RAQAMLI MASHINASOZLIKDA: AVTOMATLASHTIRISH, INTELLELTUAL TIZIMLAR, TENDENSIYALAR, MUAMMOLAR VA YECHIMLAR",
                year: "2026-yil 1-2-may",
                pdf: "https://test.tdtu.uz/storage/pages/July2026/XSYRJsebX0GK3YbT6Bxl.pdf",
            },
            {
                label: "INTELLEKTUAL YOSHLARNING SANOAT VA TEXNOLOGIK INNOVATSIYALARNI RIVOJLANTIRISHDAGI O‘RNI",
                year: "2026-yil 17-18-aprel",
                pdf: "https://test.tdtu.uz/storage/pages/July2026/kiYU9Wk5cSaIYmmRRi84.pdf",
            },
            {
                label: "RAQAMLI TEXNOLOGIYALAR ASOSIDA YER " +
                    "OSTI BOYLIKLARI RESURSLARIDAN OQILONA FOYDALANISH VA INNOVATSION USULLARNI QO'LLASH",
                year: "2026-yil 3-4-aprel",
                pdf: "https://test.tdtu.uz/storage/pages/July2026/LqA0m6Nr2986P4MzZ0Eq.pdf",
            }
        ],
    },
    {
        year: 2025,
        conferences: [
            {
                label: "Uglevodorod qazib chiqarishni ko’paytirish, ularni qayta ishlash samaradorligini oshirish bo‘yicha innovatsion texnologik yechimlar va neft-gaz sohasi uchun kadrlar tayyorlashda klasterlarning roli",
                year: "2025-yil 20-may",
                pdf: "https://test.tdtu.uz/storage/pages/July2026/jDZnd9W5eorZusU3mCNf.pdf",
            },
            {
                label: "Innovatsion texnologiyalar asosida sanoat iqtisodiyoti va menejmentini optimallashtirish: zamonaviy yondashuvlar",
                year: "2025-yil 5-6-may",
                pdf: "https://test.tdtu.uz/storage/pages/July2026/Si9cFI0u9QHTzZgeoash.pdf",
            },
            {
                label: "«INNOVATSIYA-2025» XXIX Xalqaro ilmiy-amaliy anjumani",
                year: "2025-yil 23-24-oktabr",
                pdf: "https://test.tdtu.uz/storage/pages/July2026/69TiofmEqNTFORmH8hb2.pdf",
            }

        ],
    },
    {
        year: 2024,
        conferences: [
            {
                label: "SANOAT IQTISODIYOTI VA MENEJMENTI: MUAMMO VA YECHIMLAR",
                year: "2024-yil 23-aprel",
                pdf: "https://test.tdtu.uz/storage/pages/July2026/keDE6it0JHBorzqXstIQ.pdf",
            },
            {
                label: "Mashinasozlikda materialshunoslik, payvandlab ishlab chiqarish, materiallar olish va ishlov berishning innovatsion texnologiyalarining dolzarb muammolari-2024",
                year: "2024-yil 16-17-oktabr",
                pdf: "https://test.tdtu.uz/storage/pages/July2026/LAAHfy21CvL3RpGVBR7f.pdf",
            },
            {
                label: "Аnʼanaviy xalqaro XXVIII-ilmiy amaliy-anjuman «Innovatsiya–2024",
                year: "2024-yil 26-27-oktabr",
                pdf: "https://test.tdtu.uz/storage/pages/July2026/k62XOugZLFPB7QP1mPQK.pdf",
            }
        ],
    },
    {
        year: 2023,
        conferences: [
            {
                label: "Sanoat iqtisodiyoti va menejmenti: muammo va yechimlar",
                year: "2023-yil 2-may",
                pdf: "https://test.tdtu.uz/storage/pages/July2026/OwIjihCRtaxuIx7pJmnC.pdf",
            },
            {
                label: "Yangilanayotgan O'zbekistonda ijtimoiy-gumanitar fanlarning dolzarb masalalari",
                year: "2023-yil 8-may",
                pdf: "https://test.tdtu.uz/storage/pages/July2026/H5nY7ic44yMZNd26WpLS.pdf",
            }
        ],
    },
    {
        year: 2022,
        conferences: [
            {
                label: "Sanoat iqtisodiyoti va menejmenti: muammo va yechimlar",
                year: "2022-yil 21-may",
                pdf: "https://test.tdtu.uz/storage/pages/July2026/ltmeQ3xWvYOHQPVn86O3.pdf",
            },
        ],
    },
    {
        year: 2021,
        conferences: [
            {
                label: "Sanoat iqtisodiyoti va menejmenti: muammo va yechimlar",
                year: "2021-yil 8-may",
                pdf: "https://test.tdtu.uz/storage/pages/July2026/MchSEfXBFqUWorUcarYp.pdf",
            },
        ],
    },
];

/* ── component ────────────────────────────────────────────── */
function Conferences() {
    const {t} = useTranslation();
    const [openYear, setOpenYear] = useState(2026);

    const toggle = (year) => setOpenYear((prev) => (prev === year ? null : year));

    return (
        <Layout>
            <BigFotoBack
                img={bg}
                text={t("header.header__bottom__nav__item__menu.menu-3.item-9")}
            />

            <PageWrap>
                <div className="container">

                    {conferenceData.map(({year, conferences}) => (
                        <YearBlock key={year}>
                            <YearHeader
                                open={openYear === year}
                                onClick={() => toggle(year)}
                                aria-expanded={openYear === year}
                            >
                <span className="year-info">
                  {year}-{t("Magazines.yearLabel") || "yil"}
                    <span className="badge">{conferences.length}</span>
                </span>
                                <span className="chevron">
                  <ChevronIcon
                      color={openYear === year ? "#fff" : "#15523d"}
                  />
                </span>
                            </YearHeader>

                            <YearBody open={openYear === year}>
                                <CardGrid>
                                    {conferences.map((conf, idx) => (
                                        <FlipScene key={idx}>
                                            <div className="flipper">
                                                {/* FRONT */}
                                                <div className="face front">
                                                    <div className="front-icon">
                                                        <ConferenceIcon/>
                                                    </div>
                                                    <span className="front-label">{conf.label}</span>
                                                </div>

                                                {/* BACK */}
                                                <div className="face back">
                                                    <span className="back-title">{conf.label}</span>
                                                    <span className="back-year">{conf.year}</span>
                                                    <div className="back-actions">


                                                        <a
                                                            className="back-pdf"
                                                            href={conf.pdf}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                                                                 stroke="currentColor" strokeWidth="2.5"
                                                                 strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                                                <polyline points="7 10 12 15 17 10"/>
                                                                <line x1="12" y1="15" x2="12" y2="3"/>
                                                            </svg>
                                                            PDF
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        </FlipScene>
                                    ))}
                                </CardGrid>
                            </YearBody>
                        </YearBlock>
                    ))}
                </div>
            </PageWrap>
        </Layout>
    );
}

export default Conferences;